import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  Wallet,
  CheckCircle2,
  Clock,
  AlertTriangle,
  CalendarClock,
  Loader2,
  Building2,
  ShieldCheck,
  ChevronDown,
} from 'lucide-react';
import {
  fetchCustomerPortalData,
  clearCustomerSession,
  isCustomerLoggedIn,
  type CustomerPortalData,
} from '../../lib/customerPortal';
import {
  buildCustomerScheduleFrom,
  formatEmiDueExplanation,
  type FacilityDueSummary,
} from '../../lib/workspaceStore';
import { mediaSrc } from '../../lib/mediaUrl';
import { DashboardCard } from '../../NewDashBoard/dashboard/DashboardCard';
import { useTranslation } from '../../lib/i18n';

const inr = (n: number) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusStyles: Record<string, string> = {
  Paid: 'bg-emerald-100 text-emerald-800',
  Due: 'bg-amber-100 text-amber-800',
  Scheduled: 'bg-zinc-100 text-zinc-600',
};

function statsForSelection(
  customer: NonNullable<CustomerPortalData['customer']>,
  summaries: FacilityDueSummary[],
  selectedLoanId: string
) {
  const facilities =
    selectedLoanId === 'all'
      ? customer.facilities
      : customer.facilities.filter((f) => f.recordId === selectedLoanId);
  const summarySlice =
    selectedLoanId === 'all' ? summaries : summaries.filter((s) => s.recordId === selectedLoanId);

  const totalLoan = facilities.reduce((s, f) => s + f.principal, 0);
  const paidLoan = summarySlice.reduce((s, x) => s + x.cashCollected, 0);
  const pendingLoan =
    summarySlice.reduce((s, x) => s + x.remainingContract, 0) +
    (selectedLoanId === 'all' ? customer.accruedLateFees : 0);
  const paidEmiCount = summarySlice.reduce((s, x) => {
    const emi = Math.max(1, x.contractEmi);
    return s + Math.min(x.periods, Math.floor(x.cashCollected / emi));
  }, 0);
  const pendingEmiCount = summarySlice.reduce((s, x) => {
    const emi = Math.max(1, x.contractEmi);
    return s + Math.max(0, x.periods - Math.floor(x.cashCollected / emi));
  }, 0);

  return {
    totalLoan,
    paidLoan,
    pendingLoan,
    accruedLateFees: selectedLoanId === 'all' ? customer.accruedLateFees : 0,
    lateDays: selectedLoanId === 'all' ? customer.lateDays : 0,
    paidEmiCount,
    pendingEmiCount,
  };
}

const CustomerPortal: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState<CustomerPortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLoanId, setSelectedLoanId] = useState<string>('all');

  useEffect(() => {
    if (!isCustomerLoggedIn()) {
      navigate('/login');
      return;
    }
    let active = true;
    fetchCustomerPortalData()
      .then((d) => {
        if (active) setData(d);
      })
      .catch((e) => {
        if (active) setError(e instanceof Error ? e.message : 'Could not load your account.');
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [navigate]);

  const logout = () => {
    clearCustomerSession();
    navigate('/login');
  };

  const customer = data?.customer ?? null;
  const companyName = data?.company?.name || 'Your lender';

  const summaries = useMemo(() => data?.facilitySummaries ?? [], [data?.facilitySummaries]);
  const payments = useMemo(() => data?.payments ?? [], [data?.payments]);

  const filteredSummaries = useMemo(() => {
    if (selectedLoanId === 'all') return summaries;
    return summaries.filter((s) => s.recordId === selectedLoanId);
  }, [summaries, selectedLoanId]);

  const schedule = useMemo(() => {
    if (!customer) return [];
    return buildCustomerScheduleFrom(customer, payments, selectedLoanId);
  }, [customer, payments, selectedLoanId]);

  const paymentHistory = useMemo(() => {
    const ids =
      selectedLoanId === 'all'
        ? new Set(customer?.facilities.map((f) => f.recordId) ?? [])
        : new Set([selectedLoanId]);
    return payments
      .filter((p) => ids.has(p.recordId) && !p.facilityPayoff)
      .sort((a, b) => new Date(b.paidAt).getTime() - new Date(a.paidAt).getTime());
  }, [payments, customer, selectedLoanId]);

  const stats = useMemo(() => {
    if (!customer) return null;
    return statsForSelection(customer, summaries, selectedLoanId);
  }, [customer, summaries, selectedLoanId]);

  const nextDueSummary = useMemo(() => {
    const due = filteredSummaries.find((s) => s.currentDueAmount > 0);
    if (due) return due;
    return filteredSummaries.find((s) => s.nextDueDate !== '—') ?? filteredSummaries[0] ?? null;
  }, [filteredSummaries]);

  const statCards = useMemo(() => {
    if (!stats) return [];
    return [
      { label: t('portal.totalLoan'), value: inr(stats.totalLoan), icon: Wallet, accent: 'border-l-zinc-400', iconColor: 'text-zinc-600' },
      { label: t('portal.paid'), value: inr(stats.paidLoan), icon: CheckCircle2, accent: 'border-l-emerald-600', iconColor: 'text-emerald-700' },
      { label: t('portal.dueNow'), value: inr(stats.pendingLoan), icon: Clock, accent: 'border-l-amber-500', iconColor: 'text-amber-700' },
      { label: t('portal.lateFee'), value: inr(stats.accruedLateFees), icon: AlertTriangle, accent: 'border-l-rose-500', iconColor: 'text-rose-600' },
      { label: t('portal.lateDays'), value: String(stats.lateDays), icon: CalendarClock, accent: 'border-l-rose-500', iconColor: 'text-rose-600' },
      { label: t('portal.paidEmis'), value: String(stats.paidEmiCount), icon: CheckCircle2, accent: 'border-l-emerald-600', iconColor: 'text-emerald-700' },
      { label: t('portal.pendingEmis'), value: String(stats.pendingEmiCount), icon: Clock, accent: 'border-l-amber-500', iconColor: 'text-amber-700' },
    ];
  }, [stats, t]);

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-zinc-100 font-sans">
      <header className="border-b border-zinc-200 bg-white pt-[var(--safe-area-inset-top,env(safe-area-inset-top,0px))]">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-2.5">
            {data?.company?.profile?.logoUrl ? (
              <img src={mediaSrc(data.company.profile.logoUrl)} alt="" className="h-9 w-9 rounded-md object-contain" />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-600 text-white">
                <Building2 className="h-5 w-5" />
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-zinc-900">{companyName}</p>
              <p className="text-[11px] text-zinc-500">{t('portal.yourAccount')} · {t('common.readOnly')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <LogOut className="h-4 w-4" />
            {t('portal.signOut')}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-24 text-zinc-500">
            <Loader2 className="h-5 w-5 animate-spin" />
            {t('common.loading')}
          </div>
        ) : error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-center text-sm text-rose-700">
            {error}
          </div>
        ) : (
          <>
            <div className="mb-5 flex flex-col gap-1">
              <h1 className="text-xl font-semibold text-zinc-900">
                Hello{data?.account?.name ? `, ${data.account.name}` : ''}
              </h1>
              <p className="text-sm text-zinc-500">{data?.account?.email}</p>
            </div>

            <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
              <ShieldCheck className="h-3.5 w-3.5" />
              {t('common.readOnly')}
            </div>

            {!customer ? (
              <div className="rounded-xl border border-dashed border-zinc-300 bg-white p-10 text-center text-sm text-zinc-500">
                No active loan facilities are linked to your account yet.
              </div>
            ) : (
              <>
                {customer.facilities.length > 0 ? (
                  <div className="mb-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
                    <label htmlFor="loan-select" className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                      <ChevronDown className="h-3.5 w-3.5" />
                      {t('common.selectLoan')}
                    </label>
                    <select
                      id="loan-select"
                      value={selectedLoanId}
                      onChange={(e) => setSelectedLoanId(e.target.value)}
                      className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2.5 text-sm text-zinc-900"
                    >
                      <option value="all">{t('portal.allLoans')} ({customer.facilities.length})</option>
                      {customer.facilities.map((f) => (
                        <option key={f.recordId} value={f.recordId}>
                          {f.title} · {inr(f.emi)}/mo × {f.periods}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}

                {nextDueSummary ? (
                  <section className="mb-4 rounded-xl border border-amber-200 bg-amber-50/90 p-4 shadow-sm">
                    <h2 className="text-sm font-semibold text-amber-950">Current EMI due</h2>
                    <p className="mt-1 text-2xl font-bold tabular-nums text-amber-950">
                      {nextDueSummary.currentDueAmount > 0 ? inr(nextDueSummary.currentDueAmount) : inr(0)}
                    </p>
                    <p className="mt-1 text-xs text-amber-900/90">{formatEmiDueExplanation(nextDueSummary)}</p>
                    {nextDueSummary.creditBalance > 0 ? (
                      <p className="mt-1 text-xs font-medium text-emerald-800">
                        Prepaid credit: {inr(nextDueSummary.creditBalance)} (reduces next month)
                      </p>
                    ) : null}
                  </section>
                ) : null}

                <section className="mb-6 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-2.5 lg:grid-cols-4">
                  {statCards.map((s) => (
                    <DashboardCard
                      key={s.label}
                      title={s.label}
                      value={s.value}
                      icon={s.icon}
                      accentTop={`border-l-[3px] ${s.accent}`}
                      iconColor={s.iconColor}
                      onClick={() => {}}
                    />
                  ))}
                </section>

                <section className="mb-6 rounded-xl border border-zinc-200 bg-white shadow-sm">
                  <div className="border-b border-zinc-200 px-4 py-3">
                    <h2 className="text-sm font-semibold text-zinc-900">{t('customer.facilityTable')}</h2>
                  </div>
                  <div className="divide-y divide-zinc-100">
                    {(selectedLoanId === 'all'
                      ? customer.facilities
                      : customer.facilities.filter((f) => f.recordId === selectedLoanId)
                    ).map((f) => {
                      const sum = summaries.find((s) => s.recordId === f.recordId);
                      return (
                        <div key={f.recordId} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-zinc-900">{f.title}</p>
                            <p className="text-xs text-zinc-500">
                              {inr(f.principal)} · EMI {inr(f.emi)} × {f.periods} mo · from {fmtDate(f.startDate)}
                            </p>
                            {sum ? (
                              <p className="mt-1 text-[11px] font-medium text-amber-800">{formatEmiDueExplanation(sum)}</p>
                            ) : null}
                          </div>
                          {sum && sum.currentDueAmount > 0 ? (
                            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-900">
                              Due {inr(sum.currentDueAmount)}
                            </span>
                          ) : sum && sum.creditBalance > 0 ? (
                            <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                              Credit {inr(sum.creditBalance)}
                            </span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </section>

                {paymentHistory.length > 0 ? (
                  <section className="mb-6 rounded-xl border border-zinc-200 bg-white shadow-sm">
                    <div className="border-b border-zinc-200 px-4 py-3">
                      <h2 className="text-sm font-semibold text-zinc-900">{t('portal.payments')}</h2>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-left text-sm">
                        <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                          <tr>
                            <th className="px-4 py-2">{t('common.date')}</th>
                            <th className="px-4 py-2">{t('common.amount')}</th>
                            <th className="px-4 py-2">{t('customer.balanceAfter')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-zinc-100">
                          {paymentHistory.map((p) => (
                            <tr key={p.id}>
                              <td className="px-4 py-2 text-zinc-800">{fmtDate(p.paidAt)}</td>
                              <td className="px-4 py-2 font-medium text-zinc-900">{inr(p.amount)}</td>
                              <td className="px-4 py-2 text-zinc-600">
                                {p.carriedAfter != null
                                  ? p.carriedAfter > 0
                                    ? `Owe ${inr(p.carriedAfter)}`
                                    : p.carriedAfter < 0
                                      ? `Credit ${inr(Math.abs(p.carriedAfter))}`
                                      : 'Settled'
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                ) : null}

                <section className="rounded-xl border border-zinc-200 bg-white shadow-sm">
                  <div className="flex items-center justify-between border-b border-zinc-200 px-4 py-3">
                    <h2 className="text-sm font-semibold text-zinc-900">{t('portal.schedule')}</h2>
                    <span className="text-xs text-zinc-500">
                      {schedule.filter((r) => r.status === 'Paid').length} {t('portal.paid').toLowerCase()} ·{' '}
                      {schedule.filter((r) => r.status !== 'Paid').length} {t('portal.pendingEmis').toLowerCase()}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase tracking-wide text-zinc-500">
                        <tr>
                          <th className="px-4 py-2">#</th>
                          <th className="px-4 py-2">{t('customer.due')}</th>
                          <th className="px-4 py-2">{t('customer.facility')}</th>
                          <th className="px-4 py-2">{t('common.amount')}</th>
                          <th className="px-4 py-2">{t('common.status')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {schedule.map((row, i) => (
                          <tr key={`${row.recordId}-${row.date}-${i}`} className="hover:bg-zinc-50/70">
                            <td className="px-4 py-2 text-zinc-500">{i + 1}</td>
                            <td className="px-4 py-2 text-zinc-800">{fmtDate(row.date)}</td>
                            <td className="px-4 py-2 text-zinc-600">{row.facility}</td>
                            <td className="px-4 py-2 font-medium text-zinc-900">{inr(row.amount)}</td>
                            <td className="px-4 py-2">
                              <span
                                className={`rounded px-2 py-0.5 text-[11px] font-semibold ${
                                  statusStyles[row.status] || 'bg-zinc-100 text-zinc-600'
                                }`}
                              >
                                {row.status === 'Paid' ? t('status.paid') : row.status === 'Due' ? t('status.due') : row.status === 'Scheduled' ? t('status.scheduled') : row.status}
                                {row.status === 'Due' && row.amount !== summaries.find((s) => s.recordId === row.recordId)?.contractEmi
                                  ? ' · adjusted'
                                  : ''}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {schedule.length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-500">
                              No installments scheduled.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default CustomerPortal;
